import { useParams, useNavigate } from 'react-router-dom';
import * as api from '@/lib/api';
import { supabase } from '@/lib/supabase';
import type { Season } from '@/lib/mock-data';
import {
  ArrowLeft,
  Users,
  Trophy,
  Calendar,
  Copy,
  Share2,
  Settings,
  PlusCircle,
  Play,
  ClipboardList,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import Navigation from '@/components/Navigation';
import RoundCard from '@/components/RoundCard';
import { useToast } from '@/hooks/use-toast';
import { useStore } from '@/lib/store';
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { usePlayback } from '@/lib/playback';
import { extractYouTubeId } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
} from 'recharts';

const LeagueDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { state, getSubmissionsByRound, createRound, updateLeague, updateRound, addSubmissionsToState, addVotesToState } = useStore();
  const { state: playbackState, setPlaylist } = usePlayback();
  const { currentUser } = state;
  const [selectedSeason, setSelectedSeason] = useState<'all' | string>('all');
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [isLoadingSeasons, setIsLoadingSeasons] = useState(true);
  const [isLoadingSeasonData, setIsLoadingSeasonData] = useState(false);
  const [isCreateRoundOpen, setIsCreateRoundOpen] = useState(false);
  const [isCreateSeasonOpen, setIsCreateSeasonOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [newRoundData, setNewRoundData] = useState({
    theme: '',
    description: '',
    roundNumber: 1,
    season: '',
    submissionDeadline: '',
    votingDeadline: '',
    setAsCurrent: true,
  });
  
  // Find the league
  const league = useMemo(() => {
    return state.leagues.find(l => l.id === id);
  }, [state.leagues, id]);
  
  const [isCheckingLeague, setIsCheckingLeague] = useState(false);
  
  // Redirect to dashboard if league not found (especially on refresh)
  useEffect(() => {
    if (!id) return;
    
    // If league is not in store, try fetching it immediately
    if (!league && !isCheckingLeague) {
      setIsCheckingLeague(true);
      const fetchLeague = async () => {
        try {
          const fetchedLeague = await api.leagueAPI.getLeague(id);
          if (!fetchedLeague) {
            // League doesn't exist, redirect to dashboard immediately
            console.log('[LeagueDetail] League not found, redirecting to dashboard');
            navigate('/dashboard', { replace: true });
            return;
          }
          // If league exists, it will be added to store and component will re-render
          setIsCheckingLeague(false);
        } catch (error) {
          console.error('[LeagueDetail] Error fetching league:', error);
          // On error, redirect to dashboard immediately
          navigate('/dashboard', { replace: true });
        }
      };
      
      fetchLeague();
    } else if (league) {
      setIsCheckingLeague(false);
    }
  }, [id, league, navigate, isCheckingLeague]);
  
  // Get all rounds for this league
  const leagueRounds = useMemo(() => {
    if (!league) return [];
    return state.rounds.filter((round) => round.league_id === league.id);
  }, [league, state.rounds]);

  // Fetch seasons for this league
  useEffect(() => {
    if (!league?.id) {
      setIsLoadingSeasons(false);
      return;
    }

    const fetchSeasons = async () => {
      setIsLoadingSeasons(true);
      try {
        const leagueSeasons = await api.seasonAPI.getSeasonsByLeague(league.id);
        console.log('[LeagueDetail] Fetched seasons:', {
          leagueId: league.id,
          seasonCount: leagueSeasons.length,
          seasons: leagueSeasons.map(s => ({ id: s.id, name: s.name, status: s.status }))
        });
        
        // Deduplicate seasons by name (keep the most recent one)
        const seasonMap = new Map<string, Season>();
        for (const season of leagueSeasons) {
          const existing = seasonMap.get(season.name);
          if (!existing || new Date(season.created_at) > new Date(existing.created_at)) {
            seasonMap.set(season.name, season);
          }
        }
        const uniqueSeasons = Array.from(seasonMap.values());
        
        // Clean up descriptions - remove "Imported from CSV data" text
        const cleanedSeasons = uniqueSeasons.map(season => ({
          ...season,
          description: season.description?.replace(/Imported from CSV data\.?\s*/i, '').trim() || undefined
        }));
        
        setSeasons(cleanedSeasons);
        
        // Check if user is admin of this league (either creator or has is_admin flag)
        if (currentUser?.id) {
          const isCreator = league.created_by === currentUser.id;
          
          const { data: membership } = await supabase
            .from('league_members')
            .select('is_admin')
            .eq('league_id', league.id)
            .eq('user_id', currentUser.id)
            .maybeSingle();
          
          setIsAdmin(isCreator || membership?.is_admin || false);
          
          console.log('[LeagueDetail] Admin check:', {
            userId: currentUser.id,
            leagueId: league.id,
            isCreator,
            membershipIsAdmin: membership?.is_admin,
            finalIsAdmin: isCreator || membership?.is_admin || false
          });
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Failed to fetch seasons:', error);
        setSeasons([]);
      } finally {
        setIsLoadingSeasons(false);
      }
    };

    fetchSeasons();
  }, [league?.id, currentUser?.id]);

  // Track if we've already fetched submissions to prevent duplicate calls
  const submissionsFetchedRef = useRef<Set<string>>(new Set());
  const roundsFetchedRef = useRef<Set<string>>(new Set());
  
  // Helper function to load data for rounds
  const loadRoundData = useCallback(async (roundIds: string[]) => {
    const isSupabaseConfigured = () => {
      const url = import.meta.env.VITE_SUPABASE_URL;
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
      return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
    };
    
    if (!isSupabaseConfigured()) return;

    // Filter out rounds we've already fetched
    const roundsToLoad = roundIds.filter(id => !roundsFetchedRef.current.has(id));
    if (roundsToLoad.length === 0) return;

    // Refresh round statuses
    for (const roundId of roundsToLoad) {
      try {
        const latestRound = await api.roundAPI.getRound(roundId);
        if (latestRound) {
          updateRound(latestRound.id, latestRound);
          roundsFetchedRef.current.add(roundId);
        }
        // Small delay between requests
        await new Promise(resolve => setTimeout(resolve, 50));
      } catch (error) {
        console.error(`Failed to refresh round ${roundId}:`, error);
      }
    }

    // Fetch submissions for rounds we haven't fetched yet
    const submissionsToLoad = roundsToLoad.filter(id => !submissionsFetchedRef.current.has(id));
    if (submissionsToLoad.length > 0) {
      const submissionsArrays = await Promise.all(
        submissionsToLoad.map(roundId => 
          api.submissionAPI.getSubmissionsByRound(roundId)
            .then(submissions => {
              submissionsFetchedRef.current.add(roundId);
              return submissions;
            })
            .catch(error => {
              console.error(`Failed to fetch submissions for round ${roundId}:`, error);
              return [];
            })
        )
      );
      
      // Flatten and add all submissions to state
      const allSubmissions = submissionsArrays.flat();
      if (allSubmissions.length > 0) {
        addSubmissionsToState(allSubmissions);
      }

      // Fetch votes for completed rounds (needed for results)
      // Get latest round statuses from state
      const completedRoundIds = submissionsToLoad.filter(roundId => {
        const round = state.rounds.find(r => r.id === roundId);
        return round?.status === 'completed';
      });

      if (completedRoundIds.length > 0) {
        const votesArrays = await Promise.all(
          completedRoundIds.map(roundId =>
            api.voteAPI.getVotesByRound(roundId)
              .catch(error => {
                console.error(`Failed to fetch votes for round ${roundId}:`, error);
                return [];
              })
        )
        );
        
        const allVotes = votesArrays.flat();
        if (allVotes.length > 0) {
          addVotesToState(allVotes);
        }
      }
    }
  }, [updateRound, addSubmissionsToState, addVotesToState, state.rounds]);

  // Fetch all rounds from Supabase when league loads
  // This ensures imported seasons show correct stats
  useEffect(() => {
    if (!league) return;
    
    const isSupabaseConfigured = () => {
      const url = import.meta.env.VITE_SUPABASE_URL;
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
      return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
    };
    
    if (!isSupabaseConfigured()) return;

    const fetchAllRounds = async () => {
      try {
        // Fetch all rounds for this league from database
        const allRounds = await api.roundAPI.getRoundsByLeague(league.id);
        console.log('[LeagueDetail] Fetched all rounds:', {
          leagueId: league.id,
          roundCount: allRounds.length,
          rounds: allRounds.map(r => ({ id: r.id, theme: r.theme, season_id: (r as any).season_id, season: r.season }))
        });
        
        // Add all rounds to store
        for (const round of allRounds) {
          const existingRound = state.rounds.find(r => r.id === round.id);
          if (!existingRound) {
            updateRound(round.id, round);
          } else {
            // Update existing round to ensure we have latest data
            updateRound(round.id, round);
          }
        }
        
        // Now load data for all rounds
        const roundIds = allRounds.map(r => r.id);
        loadRoundData(roundIds);
      } catch (error) {
        console.error('Failed to fetch all rounds:', error);
        // Fallback to loading rounds from store
        const roundIds = leagueRounds.map(round => round.id);
        loadRoundData(roundIds);
      }
    };

    fetchAllRounds();
  }, [league?.id]); // Only depend on league.id to prevent infinite loops

  // Track if we're currently loading season data to prevent duplicate calls
  const isLoadingSeasonDataRef = useRef(false);
  
  // Load all data when a season is selected
  useEffect(() => {
    if (!league || selectedSeason === 'all' || isLoadingSeasonDataRef.current) return;
    
    const isSupabaseConfigured = () => {
      const url = import.meta.env.VITE_SUPABASE_URL;
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
      return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
    };
    
    if (!isSupabaseConfigured()) return;

    isLoadingSeasonDataRef.current = true;
    setIsLoadingSeasonData(true);

    const loadSeasonData = async () => {
      try {
        // Get current seasons from state (use latest value)
        const currentSeasons = seasons.length > 0 ? seasons : await api.seasonAPI.getSeasonsByLeague(league.id);
        
        // Get the selected season
        const seasonToLoad = currentSeasons.find(s => s.id === selectedSeason);
        if (!seasonToLoad) {
          // Fallback: might be a season name string
          setIsLoadingSeasonData(false);
          isLoadingSeasonDataRef.current = false;
          return;
        }

        // Fetch all rounds for the league
        const allRounds = await api.roundAPI.getRoundsByLeague(league.id);
        
        // Filter rounds for the selected season
        const seasonRounds = allRounds.filter((round) => {
          // Check if round has season_id matching selected season
          const roundSeasonId = (round as any).season_id;
          if (roundSeasonId === seasonToLoad.id) {
            return true;
          }
          // Fallback to season name match for backwards compatibility
          return round.season === seasonToLoad.name;
        });

        // Add rounds to store if not already there
        for (const round of seasonRounds) {
          const existingRound = state.rounds.find(r => r.id === round.id);
          if (!existingRound) {
            updateRound(round.id, round);
          }
        }

        // Load all data for these rounds
        const roundIds = seasonRounds.map(r => r.id);
        await loadRoundData(roundIds);

        // Scroll to content after loading (smooth scroll)
        setTimeout(() => {
          const seasonBanner = document.querySelector('[data-season-banner]');
          if (seasonBanner) {
            seasonBanner.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } catch (error) {
        console.error('Failed to load season data:', error);
      } finally {
        setIsLoadingSeasonData(false);
        isLoadingSeasonDataRef.current = false;
      }
    };

    loadSeasonData();
  }, [selectedSeason, league?.id]); // Only depend on selectedSeason and league.id

  // Get unique season names from rounds (for backwards compatibility with old data)
  const seasonNames = useMemo(() => {
    const unique = new Set<string>();
    leagueRounds.forEach((round) => {
      unique.add(round.season || 'Season 1');
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }, [leagueRounds]);

  // Get selected season object for reuse
  const selectedSeasonObj = useMemo(() => {
    if (selectedSeason === 'all') return null;
    return seasons.find(s => s.id === selectedSeason) || null;
  }, [selectedSeason, seasons]);

  const filteredRounds = useMemo(() => {
    if (selectedSeason === 'all') {
      return leagueRounds;
    }
    // If selectedSeason is a season ID, filter by season_id
    if (selectedSeasonObj) {
      return leagueRounds.filter((round) => {
        // Check if round has season_id matching selected season
        // Or fallback to season name match for backwards compatibility
        return (round as any).season_id === selectedSeason || round.season === selectedSeasonObj.name;
      });
    }
    // Fallback to season name matching
    return leagueRounds.filter((round) => (round.season || 'Season 1') === selectedSeason);
  }, [leagueRounds, selectedSeason, seasons, selectedSeasonObj]);
  
  // Calculate leaderboard from actual data
  const leaderboard = useMemo(() => {
    if (!league) return [];
    const leaderboardRounds = selectedSeason === 'all' ? leagueRounds : filteredRounds;
    const userStats = new Map<string, { username: string; total_points: number; wins: number }>();
    
    // Get all completed rounds for this league
    const completedRounds = leaderboardRounds.filter(r => r.status === 'completed');
    
    completedRounds.forEach(round => {
      const submissions = getSubmissionsByRound(round.id);
      if (submissions.length === 0) return;
      
      // Find winner
      const sortedSubs = [...submissions].sort((a, b) => b.total_points - a.total_points);
      const winner = sortedSubs[0];
      
      // Accumulate points and wins
      submissions.forEach(sub => {
        const user = state.users.find(u => u.id === sub.user_id);
        const current = userStats.get(sub.user_id) || {
          username: user?.username || sub.username || 'Unknown',
          total_points: 0,
          wins: 0,
        };
        
        userStats.set(sub.user_id, {
          username: current.username,
          total_points: current.total_points + sub.total_points,
          wins: current.wins + (sub.id === winner.id ? 1 : 0),
        });
      });
    });
    
          return Array.from(userStats.entries())
      .map(([user_id, stats]) => ({ user_id, ...stats }))
      .sort((a, b) => b.total_points - a.total_points);
  }, [league, leagueRounds, filteredRounds, selectedSeason, state.users, getSubmissionsByRound]);
  
  // Get league members (for now, show users from state)
  const leagueMembers = useMemo(() => {
    if (!league) return [];
    return state.users.slice(0, league.member_count);
  }, [league, state.users]);

  const currentRoundSeason = league?.current_round?.season || 'Season 1';
  const currentRoundSeasonId = useMemo(() => {
    if (!league?.current_round) return null;
    const roundSeasonId = (league.current_round as any).season_id;
    if (roundSeasonId) {
      return seasons.find(s => s.id === roundSeasonId)?.id || null;
    }
    // Fallback to name matching
    return seasons.find(s => s.name === currentRoundSeason)?.id || null;
  }, [league?.current_round, seasons, currentRoundSeason]);
  
  const showCurrentRound =
    Boolean(league?.current_round) &&
    // Don't show current round if viewing a completed season
    (selectedSeason === 'all' || selectedSeasonObj?.status !== 'completed') &&
    // Only show if current round matches selected season
    (selectedSeason === 'all' || 
     selectedSeason === currentRoundSeasonId ||
     (selectedSeason === currentRoundSeason && !currentRoundSeasonId));
  // Get latest current round from store
  const latestCurrentRound = useMemo(() => {
    if (!league?.current_round) return null;
    return state.rounds.find(r => r.id === league.current_round!.id) || league.current_round;
  }, [league?.current_round, state.rounds]);

  const pastRounds = filteredRounds.filter((round) => round.status === 'completed');
  const upcomingRounds = filteredRounds.filter(
    (round) => round.status === 'submitting' && round.id !== league?.current_round?.id,
  );

  const seasonStats = useMemo(() => {
    const rounds = filteredRounds;
    const completed = rounds.filter((round) => round.status === 'completed');
    const submitting = rounds.filter((round) => round.status === 'submitting');
    const voting = rounds.filter((round) => round.status === 'voting');

    const submissionCount = rounds.reduce((total, round) => {
      return total + getSubmissionsByRound(round.id).length;
    }, 0);

    const uniqueSubmitters = new Set<string>();
    const totalPlaylistSongs = rounds.reduce((total, round) => {
      const submissions = getSubmissionsByRound(round.id);
      submissions.forEach((submission) => {
        uniqueSubmitters.add(submission.user_id);
      });
      const withVideo = submissions.filter((submission) => Boolean(submission.video_url));
      return total + withVideo.length;
    }, 0);

  const topPerformer = (() => {
      const pointsByUser = new Map<string, number>();
      completed.forEach((round) => {
        const submissions = getSubmissionsByRound(round.id);
        submissions.forEach((submission) => {
          const current = pointsByUser.get(submission.user_id) || 0;
          pointsByUser.set(submission.user_id, current + submission.total_points);
        });
      });
      const ranked = Array.from(pointsByUser.entries()).sort((a, b) => b[1] - a[1]);
      if (ranked.length === 0) return null;
      const [userId, points] = ranked[0];
      const user =
        state.users.find((candidate) => candidate.id === userId) ||
        (currentUser ? { username: currentUser.username } : { username: 'Unknown DJ' });
      return {
        name: user.username,
        points,
      };
    })();

    return {
      totalRounds: rounds.length,
      completed: completed.length,
      voting: voting.length,
      submitting: submitting.length,
      totalSubmissions: submissionCount,
      totalSubmitters: uniqueSubmitters.size,
      totalPlaylistSongs,
      topPerformer,
    };
  }, [filteredRounds, getSubmissionsByRound, state.users, currentUser]);

  const seasonPointsChartData = useMemo(() => {
    const pointsByUser = new Map<string, { name: string; points: number }>();
    filteredRounds.forEach((round) => {
      const submissions = getSubmissionsByRound(round.id);
      submissions.forEach((submission) => {
        const user =
          state.users.find((candidate) => candidate.id === submission.user_id)?.username ??
          submission.username ??
          'Unknown DJ';
        const existing = pointsByUser.get(submission.user_id);
        if (existing) {
          existing.points += submission.total_points;
        } else {
          pointsByUser.set(submission.user_id, { name: user, points: submission.total_points });
        }
      });
    });
    return Array.from(pointsByUser.values())
      .sort((a, b) => b.points - a.points)
      .map((entry) => ({
        name: entry.name,
        points: Math.round(entry.points),
      }));
  }, [filteredRounds, getSubmissionsByRound, state.users]);

  const seasonPlaylistTracks = useMemo(() => {
    return filteredRounds
      .flatMap((round) => {
        const submissions = getSubmissionsByRound(round.id);
        return submissions.map((submission) => {
          const youtubeId =
            submission.youtube_video_id ?? extractYouTubeId(submission.video_url ?? '');
          if (!youtubeId) {
            return null;
          }
          const submitter =
            state.users.find((user) => user.id === submission.user_id)?.username ??
            submission.username ??
            'Unknown DJ';

          return {
            submissionId: submission.id,
            roundId: submission.round_id,
            leagueId: league?.id,
            title: submission.song_title,
            artist: submission.artist_name,
            youtubeId,
            submitter,
            comment: submission.submitter_comment,
            thumbnail: submission.video_thumbnail,
            roundNumber: round.round_number,
          };
        });
      })
      .filter((track): track is NonNullable<typeof track> => Boolean(track));
  }, [filteredRounds, getSubmissionsByRound, league?.id, state.users, state.submissions]);

  const formatDateInput = (date: Date) => {
    const iso = date.toISOString();
    return iso.slice(0, 16);
  };

  const handleCreateRoundOpenChange = (open: boolean) => {
    setIsCreateRoundOpen(open);
    if (open && league) {
      const now = new Date();
      const submissionDefault = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
      const votingDefault = new Date(submissionDefault.getTime() + 3 * 24 * 60 * 60 * 1000);
      const defaultSeason =
        selectedSeason === 'all'
          ? league.current_round?.season || `Season ${Math.max(1, leagueRounds.length + 1)}`
          : selectedSeason;
      setNewRoundData({
        theme: '',
        description: '',
        roundNumber: leagueRounds.length + 1,
        season: defaultSeason,
        submissionDeadline: formatDateInput(submissionDefault),
        votingDeadline: formatDateInput(votingDefault),
        setAsCurrent: true,
      });
    }
  };

  const handleRoundFieldChange = (field: string, value: string | number | boolean) => {
    setNewRoundData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const currentRoundTracks = useMemo(() => {
    if (!league?.current_round) {
      return [];
    }

    const submissions = getSubmissionsByRound(league.current_round.id).filter(
      (submission) => submission.video_url,
    );

    return submissions
      .map((submission) => {
        const youtubeId =
          submission.youtube_video_id ?? extractYouTubeId(submission.video_url ?? '') ?? undefined;
        if (!youtubeId) {
          return null;
        }

        const submitter =
          state.users.find((user) => user.id === submission.user_id)?.username ??
          submission.username ??
          'Unknown DJ';

        return {
          submissionId: submission.id,
          roundId: submission.round_id,
          leagueId: league.id,
          roundNumber: league.current_round?.round_number,
          title: submission.song_title,
          artist: submission.artist_name,
          youtubeId,
          submitter,
          comment: submission.submitter_comment,
          thumbnail: submission.video_thumbnail,
        };
      })
      .filter((track): track is NonNullable<typeof track> => Boolean(track));
  }, [league, getSubmissionsByRound, state.users]);

  const currentRoundLabel = useMemo(() => {
    if (!league?.current_round) {
      return undefined;
    }
    return `${league.name} • ${league.current_round.theme}`;
  }, [league]);

  useEffect(() => {
    if (!league?.current_round) {
      return;
    }
    if (currentRoundTracks.length === 0) {
      return;
    }
    if (!playbackState.playlistId) {
      setPlaylist(currentRoundTracks, {
        id: league.current_round.id,
        label: currentRoundLabel,
        autoplay: false,
      });
    }
  }, [currentRoundTracks, league, playbackState.playlistId, setPlaylist, currentRoundLabel]);

  // Show loading while checking, redirect will happen automatically if not found
  if (!league) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-muted/20 to-background">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const copyInviteCode = async () => {
    try {
      await navigator.clipboard.writeText(league.invite_code);
      toast({
        title: "Code copied!",
        description: "Invite code has been copied to your clipboard.",
      });
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please copy the code manually.",
        variant: "destructive",
      });
    }
  };

  const isCreator = league?.created_by === currentUser?.id;

  const handleCreateRoundSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!league) {
      return;
    }

    if (!newRoundData.theme.trim()) {
      toast({
        title: 'Theme required',
        description: 'Please provide a theme for the new round.',
        variant: 'destructive',
      });
      return;
    }

    if (!newRoundData.submissionDeadline || !newRoundData.votingDeadline) {
      toast({
        title: 'Deadlines required',
        description: 'Submission and voting deadlines are required.',
        variant: 'destructive',
      });
      return;
    }

    const submissionISO = new Date(newRoundData.submissionDeadline);
    const votingISO = new Date(newRoundData.votingDeadline);

    if (Number.isNaN(submissionISO.getTime()) || Number.isNaN(votingISO.getTime())) {
      toast({
        title: 'Invalid dates',
        description: 'Please provide valid submission and voting deadlines.',
        variant: 'destructive',
      });
      return;
    }

    if (votingISO <= submissionISO) {
      toast({
        title: 'Adjust voting deadline',
        description: 'Voting deadline must be after the submission deadline.',
        variant: 'destructive',
      });
      return;
    }

    const createdRound = createRound({
      league_id: league.id,
      theme: newRoundData.theme.trim(),
      description: newRoundData.description.trim() || undefined,
      round_number: Number(newRoundData.roundNumber) || leagueRounds.length + 1,
      season: newRoundData.season.trim() || league.current_round?.season || 'Season 1',
      submission_deadline: submissionISO.toISOString(),
      voting_deadline: votingISO.toISOString(),
      status: 'submitting',
    });

    if (newRoundData.setAsCurrent) {
      updateLeague(league.id, { current_round: createdRound });
      const createdSeason = createdRound.season || 'Season 1';
      // Find season by name or ID
      const seasonObj = seasons.find(s => s.name === createdSeason);
      if (seasonObj && selectedSeason !== 'all' && selectedSeason !== seasonObj.id) {
        setSelectedSeason(seasonObj.id);
      } else if (!seasonObj && selectedSeason !== 'all' && selectedSeason !== createdSeason) {
        setSelectedSeason(createdSeason);
      }
    }

    toast({
      title: 'New round created 🎶',
      description: `Round "${createdRound.theme}" is ready for submissions.`,
    });

    setIsCreateRoundOpen(false);
  };

  const handlePlaySeasonPlaylist = () => {
    if (seasonPlaylistTracks.length === 0) {
      toast({
        title: 'No tracks to play',
        description: 'Add submissions with YouTube links or switch to a season with playable songs.',
        variant: 'destructive',
      });
      return;
    }

    const seasonName = selectedSeason === 'all' 
      ? 'All Seasons'
      : seasons.find(s => s.id === selectedSeason)?.name || selectedSeason;
    
    setPlaylist(seasonPlaylistTracks, {
      id: `season_${selectedSeason}_${league?.id ?? 'all'}`,
      label: `${league?.name || 'League'} • ${seasonName}`,
      autoplay: false,
      shuffle: false,
    });

    // Navigate to playlist player page
    navigate('/playlists/player');
  };

  const handleCopySeasonLinks = async () => {
    if (seasonPlaylistTracks.length === 0) {
      toast({
        title: 'Nothing to copy',
        description: 'No playable submissions found for the selected season.',
        variant: 'destructive',
      });
      return;
    }

    const separator = '\n';
    const text = seasonPlaylistTracks
      .map(
        (track) =>
          `${track.title} – ${track.artist} (${track.submitter})\nhttps://www.youtube.com/watch?v=${track.youtubeId}`,
      )
      .join(`${separator}${separator}`);

    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Playlist copied',
        description: 'All YouTube links are ready to share.',
      });
    } catch (error) {
      toast({
        title: 'Unable to copy',
        description: 'Please copy manually or adjust your browser permissions.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/20 to-background" data-tour="league-detail">
      <Navigation />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-foreground">{league.name}</h1>
                {isCreator && (
                  <Badge variant="secondary">Creator</Badge>
                )}
                {league.is_public && (
                  <Badge variant="outline">Public</Badge>
                )}
              </div>
              <p className="text-muted-foreground text-lg mb-4">{league.description}</p>
              
              <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-1" />
                  <span>{league.member_count}/{league.max_players} members</span>
                </div>
                <div className="flex items-center">
                  <Trophy className="h-4 w-4 mr-1" />
                  <span>{league.points_per_round} points per round</span>
                </div>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>Created {new Date(league.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={copyInviteCode}>
                <Copy className="h-4 w-4 mr-2" />
                Copy Invite Code
              </Button>
              <Button variant="outline">
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
              {isCreator && (
                <>
                  <Dialog open={isCreateRoundOpen} onOpenChange={handleCreateRoundOpenChange}>
                    <DialogTrigger asChild>
                      <Button className="btn-music">
                        <PlusCircle className="h-4 w-4 mr-2" />
                        New Round
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create a new round</DialogTitle>
                        <DialogDescription>
                          Plan the next theme and deadlines for this league.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateRoundSubmit} className="space-y-4">
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="round-theme">Theme *</Label>
                            <Input
                              id="round-theme"
                              placeholder="e.g., Throwback 2000s"
                              value={newRoundData.theme}
                              onChange={(event) => handleRoundFieldChange('theme', event.target.value)}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="round-description">Description</Label>
                            <Textarea
                              id="round-description"
                              placeholder="Tell members what you're looking for..."
                              value={newRoundData.description}
                              onChange={(event) => handleRoundFieldChange('description', event.target.value)}
                              rows={3}
                            />
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="round-number">Round number</Label>
                              <Input
                                id="round-number"
                                type="number"
                                min={1}
                                value={newRoundData.roundNumber}
                                onChange={(event) =>
                                  handleRoundFieldChange('roundNumber', Number(event.target.value))
                                }
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="round-season">Season label</Label>
                              <Input
                                id="round-season"
                                placeholder="Season 1"
                                value={newRoundData.season}
                                onChange={(event) => handleRoundFieldChange('season', event.target.value)}
                              />
                            </div>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="round-submission">Submission deadline *</Label>
                              <Input
                                id="round-submission"
                                type="datetime-local"
                                value={newRoundData.submissionDeadline}
                                onChange={(event) =>
                                  handleRoundFieldChange('submissionDeadline', event.target.value)
                                }
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="round-voting">Voting deadline *</Label>
                              <Input
                                id="round-voting"
                                type="datetime-local"
                                value={newRoundData.votingDeadline}
                                onChange={(event) =>
                                  handleRoundFieldChange('votingDeadline', event.target.value)
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="flex items-center justify-between rounded-lg border border-dashed border-muted px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-foreground">Set as current round</p>
                              <p className="text-xs text-muted-foreground">
                                Members will immediately see this round as active.
                              </p>
                            </div>
                            <Switch
                              checked={newRoundData.setAsCurrent}
                              onCheckedChange={(checked) => handleRoundFieldChange('setAsCurrent', checked)}
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsCreateRoundOpen(false)}
                          >
                            Cancel
                          </Button>
                          <Button type="submit" className="btn-music">
                            Create round
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                  <Button variant="outline">
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Seasons Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Seasons</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Click on a season to view all its rounds, submissions, and results
                </p>
              </div>
              {isAdmin && (
                <Button 
                  onClick={() => navigate(`/seasons/create?leagueId=${league.id}`)}
                  className="btn-music"
                  size="sm"
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Create Season
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingSeasons ? (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <p className="text-sm text-muted-foreground mt-2">Loading seasons...</p>
              </div>
            ) : seasons.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground mb-4">No seasons yet</p>
                {isAdmin && (
                  <Button 
                    onClick={() => navigate(`/seasons/create?leagueId=${league.id}`)}
                    className="btn-music"
                    size="sm"
                  >
                    <PlusCircle className="h-4 w-4 mr-2" />
                    Create First Season
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {(() => {
                  // Sort seasons by most recent to oldest
                  // Parse season names like "Spring 2022", "Summer 2023", etc.
                  const parseSeasonForSorting = (seasonName: string): { year: number; seasonIndex: number } => {
                    const seasonOrder: Record<string, number> = {
                      'winter': 0,
                      'spring': 1,
                      'summer': 2,
                      'fall': 3,
                    };
                    
                    const parts = seasonName.toLowerCase().split(' ');
                    const seasonPart = parts[0];
                    const yearPart = parts[1];
                    
                    const year = parseInt(yearPart) || 0;
                    const seasonIndex = seasonOrder[seasonPart] ?? 4;
                    
                    return { year, seasonIndex };
                  };
                  
                  const sortedSeasons = [...seasons].sort((a, b) => {
                    const aParsed = parseSeasonForSorting(a.name);
                    const bParsed = parseSeasonForSorting(b.name);
                    
                    // Sort by year descending, then by season index descending
                    if (aParsed.year !== bParsed.year) {
                      return bParsed.year - aParsed.year;
                    }
                    return bParsed.seasonIndex - aParsed.seasonIndex;
                  });
                  
                  return sortedSeasons.map((season) => {
                    // Calculate season stats
                    const seasonRounds = leagueRounds.filter((round) => {
                      const roundSeasonId = (round as any).season_id;
                      const matches = roundSeasonId === season.id || round.season === season.name;
                      return matches;
                    });
                    
                    // Log for debugging imported seasons
                    if (seasonRounds.length === 0 && season.status === 'completed') {
                      console.log('[LeagueDetail] Season has no rounds in store:', {
                        seasonId: season.id,
                        seasonName: season.name,
                        totalLeagueRounds: leagueRounds.length,
                        leagueRounds: leagueRounds.map(r => ({ id: r.id, season_id: (r as any).season_id, season: r.season }))
                      });
                    }
                    
                    const roundCount = seasonRounds.length;
                    const completedCount = seasonRounds.filter(r => r.status === 'completed').length;

                    const isSelected = selectedSeason === season.id;
                  
                  return (
                    <div
                      key={season.id}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/10 shadow-md'
                          : 'border-border hover:border-primary/50 hover:bg-muted/30'
                      }`}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedSeason('all');
                        } else {
                          setSelectedSeason(season.id);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={`font-semibold ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                              {season.name}
                            </h3>
                            <Badge 
                              variant={
                                season.status === 'completed' ? 'secondary' :
                                season.status === 'active' ? 'default' : 'outline'
                              }
                              className="text-xs"
                            >
                              {season.status === 'active' && 'Active'}
                              {season.status === 'completed' && 'Completed'}
                              {season.status === 'upcoming' && 'Upcoming'}
                            </Badge>
                            {isSelected && (
                              <Badge variant="default" className="text-xs bg-primary">
                                Viewing
                              </Badge>
                            )}
                          </div>
                          {season.description && (
                            <p className="text-sm text-muted-foreground mb-2">{season.description}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {roundCount} round{roundCount !== 1 ? 's' : ''}
                              {completedCount > 0 && (
                                <span className="text-green-600 dark:text-green-400">
                                  • {completedCount} completed
                                </span>
                              )}
                            </span>
                          </div>
                          {isSelected && roundCount > 0 && (
                            <p className="text-xs text-primary mt-2 font-medium">
                              Click again to view all seasons
                            </p>
                          )}
                        </div>
                        {season.status !== 'completed' && (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge variant="outline" className="text-xs font-mono">{season.invite_code}</Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                  });
                })()}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Season Summary Banner - Show when a specific season is selected */}
        {selectedSeason !== 'all' && !isLoadingSeasonData && selectedSeasonObj && (() => {
          const seasonRounds = filteredRounds;
          const completedRounds = seasonRounds.filter(r => r.status === 'completed');
          const totalSubmissions = seasonRounds.reduce((sum, round) => {
            return sum + getSubmissionsByRound(round.id).length;
          }, 0);
          
          return (
            <Card className="mb-6 border-2 border-primary/30 bg-gradient-to-r from-primary/5 to-blue-500/5" data-season-banner>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-xl font-bold text-foreground">{selectedSeasonObj.name}</h2>
                      <Badge 
                        variant={
                          selectedSeasonObj.status === 'completed' ? 'secondary' :
                          selectedSeasonObj.status === 'active' ? 'default' : 'outline'
                        }
                      >
                        {selectedSeasonObj.status === 'active' && 'Active'}
                        {selectedSeasonObj.status === 'completed' && 'Completed'}
                        {selectedSeasonObj.status === 'upcoming' && 'Upcoming'}
                      </Badge>
                    </div>
                    {selectedSeasonObj.description && (
                      <p className="text-sm text-muted-foreground mb-3">{selectedSeasonObj.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <span className="text-muted-foreground">
                        <span className="font-semibold text-foreground">{seasonRounds.length}</span> rounds
                      </span>
                      <span className="text-muted-foreground">
                        <span className="font-semibold text-foreground">{completedRounds.length}</span> completed
                      </span>
                      <span className="text-muted-foreground">
                        <span className="font-semibold text-foreground">{totalSubmissions}</span> submissions
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setSelectedSeason('all')}
                    >
                      View All Seasons
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleCopySeasonLinks}
                      disabled={seasonPlaylistTracks.length === 0}
                    >
                      <ClipboardList className="mr-2 h-4 w-4" />
                      Copy Links
                    </Button>
                    <Button 
                      className="btn-music" 
                      size="sm" 
                      onClick={handlePlaySeasonPlaylist}
                      disabled={seasonPlaylistTracks.length === 0}
                    >
                      <Play className="mr-2 h-4 w-4" />
                      Play Season
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Season Filter */}
        {(seasons.length > 0 || seasonNames.length > 0) && (
          <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-muted-foreground">Quick filter:</span>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedSeason === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedSeason('all')}
                  disabled={isLoadingSeasonData}
                >
                  All seasons
                </Button>
                {seasons.length > 0 ? (
                  seasons.map((season) => (
                    <Button
                      key={season.id}
                      variant={selectedSeason === season.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedSeason(selectedSeason === season.id ? 'all' : season.id)}
                      disabled={isLoadingSeasonData}
                    >
                      {season.name}
                    </Button>
                  ))
                ) : (
                  seasonNames.map((seasonName) => (
                    <Button
                      key={seasonName}
                      variant={selectedSeason === seasonName ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedSeason(selectedSeason === seasonName ? 'all' : seasonName)}
                      disabled={isLoadingSeasonData}
                    >
                      {seasonName}
                    </Button>
                  ))
                )}
              </div>
              {isLoadingSeasonData && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
                  <span>Loading season data...</span>
                </div>
              )}
            </div>
          </div>
        )}

        {isLoadingSeasonData && selectedSeason !== 'all' ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
            <p className="text-muted-foreground">Loading season data...</p>
          </div>
        ) : (
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Current Round */}
            {showCurrentRound && latestCurrentRound && (
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Current Round</h2>
                  <RoundCard
                    league={league}
                    round={latestCurrentRound} 
                    leagueId={league.id}
                    isCurrentRound={true}
                    seasonStatus={selectedSeasonObj?.status}
                  />
                {currentRoundTracks.length > 0 ? (
                  <div className="mt-6 space-y-4">
                    {playbackState.playlistId !== league.current_round.id && (
                      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            Ready to listen to this week&apos;s submissions?
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Launch the round playlist with autoplay and shuffle controls.
                          </p>
                        </div>
                        <Button
                          onClick={() => {
                            setPlaylist(currentRoundTracks, {
                              id: league.current_round!.id,
                              label: currentRoundLabel,
                              autoplay: false,
                              shuffle: playbackState.isShuffled,
                            });
                            // Navigate to playlist player page
                            navigate('/playlists/player');
                          }}
                        >
                          Play Round Playlist
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-6 rounded-lg border border-dashed border-muted-foreground/30 p-6 text-center text-sm text-muted-foreground">
                    Submissions with YouTube links will appear here once added.
                  </div>
                )}
              </div>
            )}

            {league.current_round && !showCurrentRound && selectedSeason !== 'all' && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <p className="text-foreground font-medium mb-1">Current round in different season</p>
                    <p className="text-muted-foreground">
                      The current round belongs to <span className="font-medium text-foreground">{currentRoundSeason}</span>.{" "}
                      <button
                        type="button"
                        className="text-primary hover:text-primary/80 underline underline-offset-2 font-medium"
                        onClick={() => {
                          const seasonObj = seasons.find(s => s.name === currentRoundSeason);
                          setSelectedSeason(seasonObj?.id || currentRoundSeason);
                        }}
                      >
                        Switch to that season
                      </button>{" "}
                      to view its details and playlist.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const seasonObj = seasons.find(s => s.name === currentRoundSeason);
                      setSelectedSeason(seasonObj?.id || currentRoundSeason);
                    }}
                  >
                    View Season
                  </Button>
                </div>
              </div>
            )}

            {/* Past Rounds */}
            {pastRounds.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">Past Rounds</h2>
                <div className="space-y-4">
                  {pastRounds.map((round) => {
                    // Get latest round from store
                    const latestRound = state.rounds.find(r => r.id === round.id) || round;
                    return (
                      <RoundCard
                        key={round.id}
                        round={latestRound}
                        leagueId={league.id}
                        isCurrentRound={false}
                        league={league}
                        seasonStatus={selectedSeasonObj?.status}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Upcoming Rounds */}
            {upcomingRounds.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">Upcoming Rounds</h2>
                <div className="space-y-4">
                  {upcomingRounds.map((round) => {
                    // Get latest round from store
                    const latestRound = state.rounds.find(r => r.id === round.id) || round;
                    return (
                      <RoundCard
                        key={round.id}
                        round={latestRound}
                        leagueId={league.id}
                        isCurrentRound={false}
                        league={league}
                        seasonStatus={selectedSeasonObj?.status}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty State - No Rounds */}
            {filteredRounds.length === 0 && !isLoadingSeasonData && (
              <Card className="border-dashed">
                <CardContent className="pt-12 pb-12">
                  <div className="text-center">
                    <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {selectedSeason === 'all' 
                        ? 'No rounds yet' 
                        : `No rounds in ${seasons.find(s => s.id === selectedSeason)?.name || selectedSeason}`}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                      {selectedSeason === 'all'
                        ? 'Get started by creating your first round! Rounds are where members submit songs based on themes and vote for their favorites.'
                        : selectedSeasonObj?.status === 'upcoming'
                        ? 'This season is upcoming and doesn\'t have any rounds yet. Rounds will appear here once they\'re created.'
                        : 'This season doesn\'t have any rounds yet. Check back later or contact the league admin.'}
                    </p>
                    {isAdmin && selectedSeason === 'all' && (
                      <Button 
                        onClick={() => setIsCreateRoundOpen(true)}
                        className="btn-music"
                      >
                        <PlusCircle className="h-4 w-4 mr-2" />
                        Create First Round
                      </Button>
                    )}
                    {selectedSeason !== 'all' && (
                      <Button 
                        variant="outline"
                        onClick={() => setSelectedSeason('all')}
                      >
                        View All Seasons
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Leaderboard */}
            <Card className="border-2 border-primary/30 bg-gradient-to-br from-purple-100/40 to-blue-100/40 dark:from-purple-950/20 dark:to-blue-950/20 shadow-md">
              <CardHeader className="bg-gradient-to-r from-primary/15 to-blue-500/15 dark:from-primary/20 dark:to-blue-400/20 rounded-t-lg border-b border-primary/30">
                <CardTitle className="flex items-center text-primary">
                  <Trophy className="h-5 w-5 mr-2" />
                  Leaderboard
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                                      {leaderboard.map((member, index) => {
                    const user = state.users.find(u => u.id === member.user_id);
                    const isCurrentUser = currentUser ? member.user_id === currentUser.id : false;
                    
                    return (
                      <div
                        key={member.user_id}
                        className={`flex items-center space-x-3 p-3 rounded-lg ${
                          isCurrentUser ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted/50'
                        }`}
                      >
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          index === 0 ? 'bg-yellow-500 text-white' :
                          index === 1 ? 'bg-gray-400 text-white' :
                          index === 2 ? 'bg-orange-500 text-white' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {index + 1}
                        </div>
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user?.avatar_url} />
                          <AvatarFallback className="bg-music-gradient text-white text-xs">
                            {member.username.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${
                            isCurrentUser ? 'text-primary' : 'text-foreground'
                          }`}>
                            {member.username}
                            {isCurrentUser && ' (You)'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {member.total_points} pts • {member.wins} wins
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Season Overview */}
            <Card className="border-2 border-blue-300/40 bg-gradient-to-br from-blue-100/40 to-indigo-100/40 dark:from-blue-950/20 dark:to-indigo-950/20 shadow-md">
              <CardHeader className="bg-gradient-to-r from-blue-500/15 to-indigo-500/15 dark:from-blue-400/20 dark:to-indigo-400/20 rounded-t-lg border-b border-blue-300/40">
                <CardTitle className="text-blue-600 dark:text-blue-400">Season Overview</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Snapshot of rounds, submissions, and top performers for the selected season.
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="rounded-lg border border-muted bg-muted/40 p-3">
                    <p className="text-xs uppercase text-muted-foreground tracking-wide">Rounds</p>
                    <p className="mt-1 text-2xl font-semibold text-foreground">
                      {seasonStats.totalRounds}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {seasonStats.completed} completed • {seasonStats.voting} voting • {seasonStats.submitting}{' '}
                      open
                    </p>
                  </div>
                  <div className="rounded-lg border border-muted bg-muted/40 p-3">
                    <p className="text-xs uppercase text-muted-foreground tracking-wide">Submissions</p>
                    <p className="mt-1 text-2xl font-semibold text-foreground">{seasonStats.totalSubmissions}</p>
                    <p className="text-xs text-muted-foreground">
                      {seasonStats.totalSubmitters} unique submitters • {seasonStats.totalPlaylistSongs} playable tracks
                    </p>
                  </div>
                  {seasonStats.topPerformer ? (
                    <div className="rounded-lg border border-muted bg-muted/40 p-3 col-span-2">
                      <p className="text-xs uppercase text-muted-foreground tracking-wide">Top Performer</p>
                      <p className="mt-1 text-lg font-semibold text-foreground">
                        {seasonStats.topPerformer.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {Math.round(seasonStats.topPerformer.points)} total points across completed rounds
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-muted-foreground/40 p-3 text-xs text-muted-foreground col-span-2">
                      No completed rounds yet. Results will appear here once voting is finished.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Season Points Chart */}
            {seasonPointsChartData.length > 0 && (
              <Card className="border-2 border-indigo-300/40 bg-gradient-to-br from-indigo-100/30 to-purple-100/30 dark:from-indigo-950/15 dark:to-purple-950/15 shadow-md">
                <CardHeader className="bg-gradient-to-r from-accent/15 via-primary/15 to-indigo-500/15 dark:from-accent/20 dark:via-primary/20 dark:to-indigo-400/20 rounded-t-lg border-b border-indigo-300/40">
                  <CardTitle className="text-indigo-600 dark:text-indigo-400">Season Points</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Total points earned by each participant across the selected season.
                  </p>
                </CardHeader>
                <CardContent className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={seasonPointsChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} width={40} />
                      <Tooltip
                        cursor={{ fill: 'var(--muted)' }}
                        contentStyle={{ background: 'var(--background)', borderRadius: 8, border: '1px solid var(--border)' }}
                      />
                      <Bar dataKey="points" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Members */}
            <Card className="border-2 border-secondary/30 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 dark:from-blue-950/15 dark:to-indigo-950/15 shadow-md">
              <CardHeader className="bg-gradient-to-r from-blue-500/15 to-indigo-500/15 dark:from-blue-400/20 dark:to-indigo-400/20 rounded-t-lg border-b border-secondary/30">
                <CardTitle className="flex items-center text-blue-600 dark:text-blue-400">
                  <Users className="h-5 w-5 mr-2" />
                  Members ({league.member_count})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {leagueMembers.map((member) => {
                    const isCreator = member.id === league.created_by;
                    return (
                      <div
                        key={member.id}
                        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/50"
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={member.avatar_url} />
                          <AvatarFallback className="bg-music-gradient text-white">
                            {member.username.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-foreground truncate">
                              {member.username}
                            </p>
                            {isCreator && (
                              <Badge variant="secondary" className="text-xs">Creator</Badge>
                            )}
                          </div>
                          {member.favorite_genre && (
                            <p className="text-xs text-muted-foreground">
                              {member.favorite_genre}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Invite Code Card */}
            <Card className="border-2 border-primary/30 bg-gradient-to-br from-purple-100/40 to-blue-100/40 dark:from-purple-950/20 dark:to-blue-950/20 shadow-md">
              <CardHeader className="bg-gradient-to-r from-primary/15 to-blue-500/15 dark:from-primary/20 dark:to-blue-400/20 rounded-t-lg border-b border-primary/30">
                <CardTitle className="text-primary">Invite Friends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Share this code to invite friends:</p>
                    <div className="flex items-center justify-between p-3 bg-background rounded-lg border">
                      <code className="text-2xl font-bold text-primary font-mono tracking-widest">
                        {league.invite_code}
                      </code>
                      <Button size="sm" variant="outline" onClick={copyInviteCode}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        )}
      </main>
    </div>
  );
};

export default LeagueDetail;


